import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { runDatasetQuery } from "@/lib/reporting/engine.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { KpiCard } from "@/components/reporting/kpi-card";
import { ChartCard } from "@/components/reporting/chart-card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Receipt, DollarSign, FileText, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";
import { useReportsRange } from "@/hooks/use-reports-range";

export const Route = createFileRoute("/_authenticated/app/reports/billing")({ component: BillingDash });

function BillingDash() {
  const { tenantId } = useScope();
  const run = useServerFn(runDatasetQuery);
  const { sinceIso: since, untilIso: until, days: windowDays } = useReportsRange(90);
  const rangeFilter = (column: string) =>
    until
      ? { type: "and" as const, conditions: [
          { type: "condition" as const, column, op: "gte" as const, value: since },
          { type: "condition" as const, column, op: "lte" as const, value: until },
        ] }
      : { type: "condition" as const, column, op: "gte" as const, value: since };

  const { data: byCategory, isLoading } = useQuery({
    queryKey: ["rpt-bill-cat", tenantId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "billing_lines",
      filters: rangeFilter("created_at"),
      group: ["category"],
      aggregations: [
        { column: "amount_cents", fn: "sum", alias: "amount" },
        { column: "qty", fn: "sum", alias: "qty" },
      ],
      limit: 50, offset: 0,
    }}),
  });

  const { data: invoices } = useQuery({
    queryKey: ["rpt-bill-inv", tenantId, since, until],
    enabled: !!tenantId,
    queryFn: () => run({ data: {
      tenantId: tenantId!, datasetKey: "invoices",
      filters: rangeFilter("created_at"),
      group: ["status"],
      aggregations: [
        { column: "id", fn: "count", alias: "count" },
        { column: "total_cents", fn: "sum", alias: "total" },
      ],
      limit: 20, offset: 0,
    }}),
  });

  const totals = useMemo(() => {
    let revenue = 0; for (const r of byCategory?.rows ?? []) revenue += Number(r.amount ?? 0);
    const invs = invoices?.rows ?? [];
    let count = 0, paid = 0, paidAmount = 0;
    for (const r of invs) {
      count += Number(r.count ?? 0);
      if (r.status === "paid") { paid = Number(r.count ?? 0); paidAmount = Number(r.total ?? 0); }
    }
    return { revenue, count, paid, paidAmount };
  }, [byCategory, invoices]);

  const catData = (byCategory?.rows ?? [])
    .map((r: any) => ({ category: r.category ?? "—", amount: +(Number(r.amount ?? 0) / 100).toFixed(2) }))
    .sort((a, b) => b.amount - a.amount);
  const statusData = (invoices?.rows ?? [])
    .map((r: any) => ({ status: r.status ?? "—", total: +(Number(r.total ?? 0) / 100).toFixed(2), count: Number(r.count ?? 0) }));

  return (
    <>
      <PageHeader title="Billing" description={`Invoice volume and revenue mix over the selected window (${windowDays} day${windowDays === 1 ? "" : "s"}).`} />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Revenue" value={totals.revenue} format="currency" icon={<DollarSign className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Invoices" value={totals.count} icon={<FileText className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Paid invoices" value={totals.paid} icon={<CheckCircle2 className="h-4 w-4" />} loading={isLoading} />
          <KpiCard label="Paid amount" value={totals.paidAmount} format="currency" icon={<Receipt className="h-4 w-4" />} loading={isLoading} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Revenue by Category">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={catData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" fontSize={11} />
                <YAxis type="category" dataKey="category" fontSize={11} width={100} />
                <Tooltip />
                <Bar dataKey="amount" fill="var(--primary)" name="Amount ($)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Invoices by Status">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="status" fontSize={11} />
                <YAxis yAxisId="l" fontSize={11} />
                <YAxis yAxisId="r" orientation="right" fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="l" dataKey="count" fill="var(--primary)" name="Count" />
                <Bar yAxisId="r" dataKey="total" fill="var(--muted-foreground)" name="Total ($)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}